import {
  BlockStack,
  Button,
  Checkbox, Divider,
  Select,
  Text,
  TextField,
} from "@shopify/polaris"
import { PlusCircleIcon } from "@shopify/polaris-icons"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"

import DraggableList from "@/component/DraggableList"
import EditorPopup from "@/component/EditorPopup"
import ModuleCard from "@/component/ModuleCard"
import type {
  ThemeBottomSectionImageItem,
  ThemeBottomSectionState,
  ThemeImageBannerRef,
} from "@/models/ITrackingPageOptions"
import { THEME_LAYOUT_LEFT_ROOT_ID } from "@/pages/TrackingPage/ThemeLayoutCustomize/components/Layout/themeLayoutLeftScroll"

import ImageUpload from "./ImageUpload"
import { newThemeBottomId } from "./themeBottomSectionDefaults"
import {
  clampThemeLayoutAltOrDestinationUrl,
  THEME_LAYOUT_ALT_AND_DESTINATION_URL_MAX_LENGTH,
} from "./themeLayoutFieldLimits"
import { useThemeLayoutSubmoduleDirty } from "./ThemeLayoutSubmoduleDirtyContext"
import { useSyncEditingImageOnCommonImageDeleted } from "./useSyncEditingImageOnCommonImageDeleted"
import { isValidYouTubeUrlInput } from "./youtubeUrlValidation"

export interface ThemeBottomSectionEditorProps {
  /** 已由父组件 merge 后的完整状态 */
  value: ThemeBottomSectionState
  onCommit: (next: ThemeBottomSectionState) => Promise<void>
  onPersistItemImage: (itemId: string, next: ThemeImageBannerRef | undefined) => Promise<void>
  /** 弹窗打开期间的预览 overlay 回调：将当前弹窗草稿推送到右侧预览；
   *  弹窗关闭时传 null 以还原 store 数据。不传则不启用预览联动。 */
  onPopupDraftChange?: (draft: ThemeBottomSectionState | null) => void
}

const ThemeBottomSectionEditor = (props: ThemeBottomSectionEditorProps) => {
  const { value: section, onCommit, onPopupDraftChange } = props
  const { t } = useTranslation("tracking_page")
  const { setSubmoduleDirty } = useThemeLayoutSubmoduleDirty()

  const [editingImageId, setEditingImageId] = useState<string | null>(null)
  const [imageSnapshot, setImageSnapshot] = useState<ThemeBottomSectionImageItem | undefined>(undefined)
  const [sectionSnapshot, setSectionSnapshot] = useState<typeof section | undefined>(undefined)
  const [imageDraftBanner, setImageDraftBanner] = useState<ThemeImageBannerRef | undefined>(undefined)
  const [imageDraftUrl, setImageDraftUrl] = useState("")
  const [imageDraftAlt, setImageDraftAlt] = useState("")
  const [imageDraftNewWin, setImageDraftNewWin] = useState(true)

  const [videoModalOpen, setVideoModalOpen] = useState(false)
  const [videoSnapshot, setVideoSnapshot] = useState<ThemeBottomSectionState["video"]>(undefined)
  const [videoDraftUrl, setVideoDraftUrl] = useState("")
  const [videoDraftAlt, setVideoDraftAlt] = useState("")
  const [videoDraftAutoplay, setVideoDraftAutoplay] = useState(false)
  const [videoDraftLoop, setVideoDraftLoop] = useState(false)
  const [videoUrlError, setVideoUrlError] = useState(false)

  // Edit image 弹窗打开期间，正在编辑的图片若在图库被删除，则同步清空弹窗内的图片
  useSyncEditingImageOnCommonImageDeleted({
    active: editingImageId !== null,
    setImageDraftBanner,
    setImageSnapshot,
  })

  const ratioSelectValue = section.imageRatio
  const ratioOptions = useMemo(
    () => [
      { value: "SQUARE", label: t("Square") },
      { value: "PORTRAIT", label: t("Portrait") },
      { value: "AUTO", label: t("AdaptToImage") },
    ],
    [t],
  )

  useEffect(() => {
    if (videoModalOpen && !section.video) {
      setVideoModalOpen(false)
    }
  }, [videoModalOpen, section.video])

  const handleRatioChange = useCallback(
    async (nextRatio: string) => {
      if (nextRatio !== "PORTRAIT" && nextRatio !== "AUTO" && nextRatio !== "SQUARE") {
        return
      }
      await onCommit({ ...section, imageRatio: nextRatio })
    },
    [section, onCommit],
  )

  const handleAddImage = useCallback(async () => {
    if (section.images.length >= 3) {
      return
    }
    const item = {
      id: newThemeBottomId(),
      destinationUrl: "",
      openInNewWindow: true,
      enabled: true,
    }

    await onCommit({
      ...section,
      images: [...section.images, item],
    })
  }, [section, onCommit])

  const handleReorderBottomImages = useCallback(
    (list: Array<{ id: string; checked: boolean }>) => {
      const nextImages = list
        .map((row) => section.images.find((i) => i.id === row.id))
        .filter((row): row is ThemeBottomSectionImageItem => Boolean(row))

      void onCommit({
        ...section,
        images: nextImages,
      })
    },
    [section, onCommit],
  )

  const handleSaveImagePopup = useCallback(async () => {
    if (!editingImageId) {
      return
    }

    const images = section.images.map((i) => {
      if (i.id === editingImageId) {
        const trimmedAlt = imageDraftAlt.trim()

        return {
          ...i,
          destinationUrl: imageDraftUrl.trim(),
          openInNewWindow: imageDraftNewWin,
          image: imageDraftBanner
            ? { ...imageDraftBanner, alt: trimmedAlt }
            : trimmedAlt ? { shopifyFileId: "", url: "", alt: trimmedAlt } : undefined,
        }
      }

      return i
    })

    await onCommit({ ...section, images })
    setSectionSnapshot(undefined)
    setImageSnapshot(undefined)
    setEditingImageId(null)
  }, [
    editingImageId,
    imageDraftAlt,
    imageDraftBanner,
    imageDraftNewWin,
    imageDraftUrl,
    onCommit,
    section,
  ])

  const openVideoPopup = useCallback(() => {
    const v = section.video

    if (!v) {
      return
    }
    setVideoDraftUrl(v.youtubeUrl)
    setVideoDraftAlt(v.alt || "")
    setVideoDraftAutoplay(v.autoplay)
    setVideoDraftLoop(v.loop)
    setVideoSnapshot({ ...v })
    setVideoUrlError(false)
    setVideoModalOpen(true)
  }, [section.video])

  const handleImageDraftBannerChange = useCallback(async (next: ThemeImageBannerRef | undefined) => {
    const normalized = next ? { ...next, alt: next.alt || "" } : next

    setImageDraftBanner(normalized)
  }, [])

  const handleImageDraftAltChange = useCallback((next: string) => {
    setImageDraftAlt(clampThemeLayoutAltOrDestinationUrl(next))
  }, [])

  const handleImageDraftUrlChange = useCallback((next: string) => {
    setImageDraftUrl(clampThemeLayoutAltOrDestinationUrl(next))
  }, [])

  const handleImageDraftNewWindowChange = useCallback((next: boolean) => {
    setImageDraftNewWin(next)
  }, [])

  const applyVideoDraft = useCallback((next: {
    youtubeUrl: string
    alt: string
    autoplay: boolean
    loop: boolean
  }) => {
    const currentVideo = section.video

    if (!currentVideo) {
      return
    }
    void onCommit({
      ...section,
      video: {
        ...currentVideo,
        youtubeUrl: next.youtubeUrl,
        alt: next.alt,
        autoplay: next.autoplay,
        loop: next.loop,
      },
    })
  }, [onCommit, section])

  const handleVideoDraftUrlChange = useCallback((next: string) => {
    setVideoDraftUrl(next)
    setVideoUrlError(false)
    applyVideoDraft({
      youtubeUrl: next,
      alt: videoDraftAlt,
      autoplay: videoDraftAutoplay,
      loop: videoDraftLoop,
    })
  }, [applyVideoDraft, videoDraftAlt, videoDraftAutoplay, videoDraftLoop])

  const handleVideoDraftAltChange = useCallback((next: string) => {
    setVideoDraftAlt(next)
    applyVideoDraft({
      youtubeUrl: videoDraftUrl,
      alt: next,
      autoplay: videoDraftAutoplay,
      loop: videoDraftLoop,
    })
  }, [applyVideoDraft, videoDraftAutoplay, videoDraftLoop, videoDraftUrl])

  const handleVideoDraftAutoplayChange = useCallback((next: boolean) => {
    setVideoDraftAutoplay(next)
    applyVideoDraft({
      youtubeUrl: videoDraftUrl,
      alt: videoDraftAlt,
      autoplay: next,
      loop: videoDraftLoop,
    })
  }, [applyVideoDraft, videoDraftAlt, videoDraftLoop, videoDraftUrl])

  const handleVideoDraftLoopChange = useCallback((next: boolean) => {
    setVideoDraftLoop(next)
    applyVideoDraft({
      youtubeUrl: videoDraftUrl,
      alt: videoDraftAlt,
      autoplay: videoDraftAutoplay,
      loop: next,
    })
  }, [applyVideoDraft, videoDraftAlt, videoDraftAutoplay, videoDraftUrl])

  const handleSaveVideoPopup = useCallback((): boolean | void => {
    const v = section.video

    if (!v) {
      return false
    }
    if (!isValidYouTubeUrlInput(videoDraftUrl)) {
      setVideoUrlError(true)
      return false
    }
    setVideoUrlError(false)
    void onCommit({
      ...section,
      video: {
        ...v,
        youtubeUrl: videoDraftUrl.trim(),
        alt: videoDraftAlt.trim(),
        autoplay: videoDraftAutoplay,
        loop: videoDraftLoop,
      },
    })
    setVideoSnapshot(undefined)
  }, [section, onCommit, videoDraftAlt, videoDraftAutoplay, videoDraftLoop, videoDraftUrl])

  const handleAddVideoBlock = useCallback(async () => {
    if (section.video) {
      return
    }
    await onCommit({
      ...section,
      video: {
        id: newThemeBottomId(),
        youtubeUrl: "",
        alt: "",
        autoplay: false,
        loop: false,
        enabled: true,
      },
    })
  }, [section, onCommit])

  const handleRemoveVideo = useCallback(async () => {
    await onCommit({
      ...section,
      video: undefined,
    })
  }, [section, onCommit])

  const handleVideoRowEnabled = useCallback(
    async (checked: boolean) => {
      const v = section.video

      if (!v) {
        return
      }
      await onCommit({
        ...section,
        video: { ...v, enabled: checked },
      })
    },
    [section, onCommit],
  )

  const handleRemoveVideoFromPopup = useCallback(async () => {
    await handleRemoveVideo()
    setVideoSnapshot(undefined)
    setVideoModalOpen(false)
  }, [handleRemoveVideo])

  // store 在弹窗期间从未被修改（预览通过 popup draft overlay 实现），
  // 因此 cancel 只需清空本地草稿状态即可，不需要调用 onCommit 恢复 store。
  const handleCancelImagePopup = useCallback(() => {
    setSectionSnapshot(undefined)
    setImageSnapshot(undefined)
    // editingImageId 由 onDismiss 中的 setEditingImageId(null) 清空，
    // 触发上方 useEffect → onPopupDraftChange(null) 清除预览 overlay。
  }, [])

  const handleCancelVideoPopup = useCallback(() => {
    if (!videoSnapshot) {
      return
    }
    void onCommit({
      ...section,
      video: { ...videoSnapshot },
    })
    setVideoSnapshot(undefined)
  }, [onCommit, section, videoSnapshot])

  const handleImageRowEnabled = useCallback(
    async (itemId: string, checked: boolean) => {
      const images = section.images.map((i) =>
        (i.id === itemId ? { ...i, enabled: checked } : i),
      )

      await onCommit({ ...section, images })
    },
    [section, onCommit],
  )

  const isImagePopupDirty = useMemo(() => {
    if (!imageSnapshot) {
      return false
    }

    const currentImage = imageSnapshot.image
    const draftImage = imageDraftBanner

    const currentAlt = currentImage?.alt?.trim() || ""
    const draftAlt = imageDraftAlt.trim()
    const currentUrl = imageSnapshot.destinationUrl.trim()
    const draftUrl = imageDraftUrl.trim()

    const hasDifferentUrl = currentUrl !== draftUrl
    const hasDifferentAlt = currentAlt !== draftAlt
    const hasDifferentOpenInNewWindow = imageSnapshot.openInNewWindow !== imageDraftNewWin
    const hasDifferentBanner =
      (currentImage?.shopifyFileId || "") !== (draftImage?.shopifyFileId || "")
      || (currentImage?.url || "") !== (draftImage?.url || "")

    return hasDifferentUrl
      || hasDifferentAlt
      || hasDifferentOpenInNewWindow
      || hasDifferentBanner
  }, [
    imageSnapshot,
    imageDraftAlt,
    imageDraftBanner,
    imageDraftNewWin,
    imageDraftUrl,
  ])

  const isVideoPopupDirty = useMemo(() => {
    const currentVideo = videoSnapshot

    if (!currentVideo) {
      return false
    }

    const currentUrl = currentVideo.youtubeUrl.trim()
    const currentAlt = (currentVideo.alt || "").trim()
    const draftUrl = videoDraftUrl.trim()
    const draftAlt = videoDraftAlt.trim()

    return currentUrl !== draftUrl
      || currentAlt !== draftAlt
      || currentVideo.autoplay !== videoDraftAutoplay
      || currentVideo.loop !== videoDraftLoop
  }, [videoSnapshot, videoDraftAlt, videoDraftAutoplay, videoDraftLoop, videoDraftUrl])
  const isAnyPopupDirty = isImagePopupDirty || isVideoPopupDirty

  useEffect(() => {
    setSubmoduleDirty("theme-bottom-section-editor-popup", isAnyPopupDirty)

    return () => {
      setSubmoduleDirty("theme-bottom-section-editor-popup", false)
    }
  }, [isAnyPopupDirty, setSubmoduleDirty])

  // 将弹窗草稿实时同步到右侧预览 overlay。
  // editingImageId 为 null 时（弹窗关闭）传 null 清除 overlay，preview 回退到 store 数据。
  // 完全不写入 store，消除了 MobX/React 时序竞态。
  useEffect(() => {
    if (!onPopupDraftChange) {
      return
    }

    if (!editingImageId) {
      onPopupDraftChange(null)

      return
    }

    onPopupDraftChange({
      ...section,
      images: section.images.map((i) => {
        if (i.id !== editingImageId) {
          return i
        }

        return {
          ...i,
          destinationUrl: imageDraftUrl,
          openInNewWindow: imageDraftNewWin,
          image: imageDraftBanner
            ? { ...imageDraftBanner, alt: imageDraftAlt }
            : imageDraftAlt.trim() ? { shopifyFileId: "", url: "", alt: imageDraftAlt } : undefined,
        }
      }),
    })
  }, [
    editingImageId,
    imageDraftBanner,
    imageDraftUrl,
    imageDraftAlt,
    imageDraftNewWin,
    section,
    onPopupDraftChange,
  ])

  const handleRemoveEditingImageRow = useCallback(async () => {
    if (!editingImageId) {
      return
    }

    await onCommit({
      ...section,
      images: section.images.filter((i) => i.id !== editingImageId),
    })
    setSectionSnapshot(undefined)
    setImageSnapshot(undefined)
    setEditingImageId(null)
  }, [editingImageId, onCommit, section])

  const handleEditImageClick = useCallback((id: string) => {
    const item = section.images.find((imageItem) => imageItem.id === id)

    if (!item) {
      return
    }
    setImageSnapshot({ ...item, image: item.image ? { ...item.image } : undefined })
    setSectionSnapshot({
      ...section,
      images: section.images.map((i) => ({ ...i, image: i.image ? { ...i.image } : undefined })),
    })
    // stub image（url 和 shopifyFileId 均为空）视为"无图片"，避免 ImageUpload 误认为有图
    const isRealBanner = Boolean(item.image?.shopifyFileId?.trim() || item.image?.url?.trim())

    setImageDraftUrl(clampThemeLayoutAltOrDestinationUrl(item.destinationUrl))
    setImageDraftAlt(clampThemeLayoutAltOrDestinationUrl(item.image?.alt || ""))
    setImageDraftBanner(isRealBanner ? item.image : undefined)
    setImageDraftNewWin(item.openInNewWindow)
    setEditingImageId(id)
  }, [section])

  const draggableImageCards = useMemo(
    () =>
      section.images.map((item) => ({
        id: item.id,
        checked: item.enabled !== false,
        children: (
          <div className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-2 pr-1">
            <div className="min-w-0 flex-1 [&_.Polaris-Labelled__LabelWrapper]:max-w-none">
              <Checkbox
                label={t("ThemeBottomImageSection")}
                checked={item.enabled !== false}
                onChange={(checked) => void handleImageRowEnabled(item.id, checked)}
              />
            </div>
            <Button variant="plain" onClick={() => handleEditImageClick(item.id)}>
              {t("ThemeBottomEdit")}
            </Button>
          </div>
        ),
      })),
    [handleEditImageClick, handleImageRowEnabled, section.images, t],
  )

  const canAddImage = section.images.length < 3
  const canAddVideo = !section.video

  const leftRootPortalEl =
    typeof document !== "undefined"
      ? document.getElementById(THEME_LAYOUT_LEFT_ROOT_ID)
      : null

  const editorPopups = (
    <>
      <EditorPopup
        open={editingImageId !== null}
        title={t("ThemeBottomEditImageTitle")}
        close={() => setEditingImageId(null)}
        onDismiss={() => {
          handleCancelImagePopup()
          setEditingImageId(null)
        }}
        active={isImagePopupDirty}
        primaryAction={{
          content: t("Done"),
          onAction: () => void handleSaveImagePopup(),
        }}
        secondaryActions={{
          content: t("ThemeBottomModalCancel"),
          onAction: handleCancelImagePopup,
        }}
      >
        <ModuleCard>
          <div className={"flex gap-3 flex-col"}>
            <div>
              <Text as="p" variant="bodyMd">
                {t("ThemeBottomImageSection")}
              </Text>
              <div className="mt-2">
                <ImageUpload
                  banner={imageDraftBanner}
                  onPersistBanner={handleImageDraftBannerChange}
                />
              </div>
            </div>
            {/* <TextField */}
            {/*   label={t("AltTextOptional")} */}
            {/*   value={imageDraftAlt} */}
            {/*   onChange={handleImageDraftAltChange} */}
            {/*   maxLength={THEME_LAYOUT_ALT_AND_DESTINATION_URL_MAX_LENGTH} */}
            {/*   autoComplete="off" */}
            {/* /> */}
            <div className={"flex gap-1 flex-col"}>
              <TextField
                label={t("ThemeBottomDestinationUrl")}
                value={imageDraftUrl}
                onChange={handleImageDraftUrlChange}
                maxLength={THEME_LAYOUT_ALT_AND_DESTINATION_URL_MAX_LENGTH}
                autoComplete="off"
                helpText={t("LeaveLink")}
              />
              <Checkbox
                label={t("ThemeBottomOpenInNewWindow")}
                checked={imageDraftNewWin}
                onChange={handleImageDraftNewWindowChange}
              />
            </div>
          </div>
        </ModuleCard>

        <div className={"p-4"}>
          <Button
            tone="critical"
            variant="secondary"
            onClick={() => void handleRemoveEditingImageRow()}
          >
            {t("RemoveThisSection")}
          </Button>
        </div>
      </EditorPopup>

      <EditorPopup
        open={videoModalOpen}
        title={t("ThemeBottomEditVideoTitle")}
        close={() => setVideoModalOpen(false)}
        onDismiss={() => {
          handleCancelVideoPopup()
          setVideoModalOpen(false)
        }}
        active={isVideoPopupDirty}
        primaryAction={{
          content: t("Done"),
          onAction: handleSaveVideoPopup,
        }}
        secondaryActions={{
          content: t("ThemeBottomModalCancel"),
          onAction: handleCancelVideoPopup,
        }}
      >
        <ModuleCard>
          <div className={"flex gap-3 flex-col"}>
            <TextField
              label={t("ThemeBottomYouTubeUrl")}
              value={videoDraftUrl}
              onChange={handleVideoDraftUrlChange}
              error={videoUrlError ? t("ThemeBottomInvalidYouTubeUrl") : undefined}
              autoComplete="off"
            />


            <TextField
              label={t("VideoAltText")}
              value={videoDraftAlt}
              onChange={handleVideoDraftAltChange}
              autoComplete="off"
              helpText={t("DescribeHelpText")}
            />
            <div className={"flex flex-col gap-1"}>
              <Checkbox
                label={t("ThemeBottomAutoplay")}
                checked={videoDraftAutoplay}
                onChange={handleVideoDraftAutoplayChange}
                helpText={t("MuteDefault")}
              />
              <Checkbox
                label={t("ThemeBottomLoopVideo")}
                checked={videoDraftLoop}
                onChange={handleVideoDraftLoopChange}
              />
            </div>
          </div>
        </ModuleCard>

        <div className={"p-4"}>
          <Button
            tone="critical"
            variant="secondary"
            onClick={() => void handleRemoveVideoFromPopup()}
          >
            {t("RemoveThisSection")}
          </Button>
        </div>
      </EditorPopup>
    </>
  )

  return (
    <BlockStack gap="400">
      <div className={"px-4"}>
        <BlockStack gap="200">
          {section.images.length > 0 ? (
            <DraggableList
              cards={draggableImageCards}
              setCards={handleReorderBottomImages}
              className="flex flex-col gap-1"
            />
          ) : null}

          <div className={"linkButton"}>
            <Button
              fullWidth
              size="large"
              disabled={!canAddImage}
              variant="secondary"
              icon={PlusCircleIcon}
              onClick={() => void handleAddImage()}
            >
              {t("ThemeBottomAddImage")}
            </Button>
          </div>

          <div className={"mt-1"}>
            <Select
              label={t("ImageRatio")}
              options={ratioOptions}
              value={ratioSelectValue}
              onChange={(v) => void handleRatioChange(v)}
            />
          </div>
        </BlockStack>
      </div>
      <Divider />

      <div className={"px-4 pb-4"}>
        <BlockStack gap="200">
          {section.video ? (
            <div className="flex w-full min-w-0 items-center justify-between gap-3 pr-1">
              <div className="min-w-0 flex-1 [&_.Polaris-Labelled__LabelWrapper]:max-w-none">
                <Checkbox
                  label={t("ThemeBottomVideoSection")}
                  checked={section.video.enabled !== false}
                  onChange={(checked) => void handleVideoRowEnabled(checked)}
                />
              </div>
              <Button variant="plain" onClick={openVideoPopup}>
                {t("ThemeBottomEdit")}
              </Button>
            </div>
          ) : null}

          <div className={"linkButton"}>
            <Button
              fullWidth
              size="large"
              disabled={!canAddVideo}
              variant="secondary"
              icon={PlusCircleIcon}
              onClick={() => void handleAddVideoBlock()}
            >
              {t("ThemeBottomAddVideo")}
            </Button>
          </div>
        </BlockStack>
      </div>

      {leftRootPortalEl ? createPortal(editorPopups, leftRootPortalEl) : editorPopups}
    </BlockStack>
  )
}

export default ThemeBottomSectionEditor
